/**
 * LOD0 Brand Image viewer discovery hook.
 *
 * Wires the CURRENT loaded parcels → persistent identities → brand-image
 * metadata → renderable territories + a decoded-texture cache, using EXACTLY
 * two bounded batch requests per discovery generation (identity discovery, then
 * brand-image metadata). There is NO per-parcel / per-territory / per-frame
 * request and NO full database scan: discovery is bounded by the loaded parcels
 * and re-runs only when the loaded parcel SET actually changes.
 *
 * Zero-stale-authority: a monotonic generation + AbortController guarantees a
 * slow/older discovery response can never overwrite a newer one. On disable /
 * unmount the texture cache is fully disposed (no resource growth, no leak).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import {
  type BrandImageMeta,
  batchBrandImages,
  batchTerritoryIdentities,
} from "../lib/brand-image-api";
import {
  associateRenderables,
  type RenderableTerritory,
  type UnitRect,
} from "./brand-image-geometry";
import { BrandImageTextureCache } from "./brand-image-textures";
import { onBrandImageMutation } from "../lib/brand-image-invalidation";

export interface BrandDiscoveryCounts {
  /** identity-discovery batch requests issued (metadata is coalesced). */
  identityBatches: number;
  /** brand-image metadata batch requests issued. */
  imageBatches: number;
  /** image-content (decode) requests issued — one per revision, image only. */
  contentRequests: number;
  /** identities discovered in the current generation. */
  identities: number;
  /** renderable territories with a ready-or-loading image. */
  renderables: number;
}

export interface UseBrandImageDiscovery {
  renderables: RenderableTerritory[];
  textures: BrandImageTextureCache;
  counts: BrandDiscoveryCounts;
}

/** Stable signature of the loaded parcel SET (order-independent). */
function parcelSetKey(parcels: readonly UnitRect[]): string {
  if (parcels.length === 0) return "";
  const ids = parcels.map((p) => p.id).sort((a, b) => a - b);
  return ids.join(",");
}

export function useBrandImageDiscovery(
  parcels: readonly UnitRect[],
  onChange: () => void,
  enabled = true,
): UseBrandImageDiscovery {
  const [renderables, setRenderables] = useState<RenderableTerritory[]>([]);
  const genRef = useRef(0);
  // Set of identity ids known to the current discovery (with or without an
  // image) — used to SCOPE live invalidation to relevant identities only.
  const discoveredIdsRef = useRef<Set<string>>(new Set());
  // Bumped by an authoritative same-app Brand Image mutation for a relevant
  // identity; forces exactly ONE bounded re-resolution of discovery.
  const [invalidationNonce, setInvalidationNonce] = useState(0);
  const countsRef = useRef<BrandDiscoveryCounts>({
    identityBatches: 0,
    imageBatches: 0,
    contentRequests: 0,
    identities: 0,
    renderables: 0,
  });

  // onChange kept in a ref so the discovery effect never re-subscribes on it.
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  const texturesRef = useRef<BrandImageTextureCache | null>(null);
  if (texturesRef.current === null) {
    texturesRef.current = new BrandImageTextureCache({
      onReady: () => onChangeRef.current(),
    });
  }
  const textures = texturesRef.current;

  // Full disposal on unmount (never leaks decoded resources).
  useEffect(() => () => textures.clear(), [textures]);

  // Live same-app invalidation: a confirmed SAVE/REPLACE/REMOVE for an identity
  // this viewer currently knows about triggers ONE bounded re-resolution. Scoped
  // by identity_id so unrelated territories never cause a discovery storm.
  useEffect(() => {
    return onBrandImageMutation(({ identityId }) => {
      if (discoveredIdsRef.current.has(identityId)) {
        setInvalidationNonce((n) => n + 1);
      }
    });
  }, []);

  const key = useMemo(() => parcelSetKey(parcels), [parcels]);
  // Snapshot the parcels for the current key (association reads geometry).
  const parcelsRef = useRef(parcels);
  parcelsRef.current = parcels;

  useEffect(() => {
    const myGen = ++genRef.current;
    if (!enabled || key === "") {
      textures.sync([]);
      setRenderables([]);
      countsRef.current = { ...countsRef.current, identities: 0, renderables: 0 };
      return;
    }
    const controller = new AbortController();
    const loaded = parcelsRef.current;

    (async () => {
      try {
        const ids = loaded.map((p) => p.id);
        countsRef.current.identityBatches += 1;
        const identities = await batchTerritoryIdentities(ids, controller.signal);
        if (myGen !== genRef.current) return; // stale generation → drop
        discoveredIdsRef.current = new Set(identities.map((i) => i.identity_id));

        const identityIds = Array.from(new Set(identities.map((i) => i.identity_id)));
        let metas: BrandImageMeta[] = [];
        if (identityIds.length > 0) {
          countsRef.current.imageBatches += 1;
          metas = await batchBrandImages(identityIds, controller.signal);
        }
        if (myGen !== genRef.current) return; // stale generation → drop

        // Texture cache reconciles to EXACTLY the discovered image set.
        textures.sync(metas);

        const dims = new Map(metas.map((m) => [m.identity_id, { width: m.width, height: m.height }]));
        const discovered = identities.map((i) => ({
          identityId: i.identity_id,
          owner: i.owner,
          memberParcelIds: i.member_parcel_ids,
        }));
        const next = associateRenderables(parcelsRef.current, discovered, dims);
        if (myGen !== genRef.current) return;

        countsRef.current = {
          ...countsRef.current,
          contentRequests: textures.contentRequestCount,
          identities: identities.length,
          renderables: next.length,
        };
        setRenderables(next);
        onChangeRef.current();
      } catch {
        if (myGen !== genRef.current || controller.signal.aborted) return;
        // Fail-soft: a discovery error simply shows no brand images this gen.
        setRenderables([]);
      }
    })();

    return () => controller.abort();
  }, [key, enabled, textures, invalidationNonce]);

  return { renderables, textures, counts: countsRef.current };
}
